import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPen } from '@fortawesome/free-solid-svg-icons';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { PriceMatrixType, ProductType } from './ProductTypes';
import '../../style/scss/price-matrix.scss';

interface ProductPriceMatrixTabProps {
    itemId: string;
    data: ProductType;
    refetchData?: () => void;
};

// Editable copy of the matrix with string cells, so the inputs keep what the user types
interface EditableMatrix {
    widths: string[];
    depths: string[];
    prices: string[][];
}

// Converts the saved matrix of the product into the editable string matrix
const toEditableMatrix = (matrix: PriceMatrixType | null | undefined): EditableMatrix => ({
    widths: matrix ? matrix.widths.map(width => String(width)) : [],
    depths: matrix ? matrix.depths.map(depth => String(depth)) : [],
    prices: matrix ? matrix.prices.map(row => row.map(price => price === null ? '' : String(price))) : [],
});

// Converts the editable string matrix into the payload of the backend, empty cells become null
const toMatrixPayload = (matrix: EditableMatrix): PriceMatrixType | null => {
    if (matrix.widths.length === 0 && matrix.depths.length === 0) return null;
    const parseNumber = (value: string): number | null => {
        const cleaned = value.trim().replace(',', '.');
        if (cleaned === '') return null;
        const parsed = Number(cleaned);
        return isNaN(parsed) ? null : parsed;
    };
    return {
        widths: matrix.widths.map(width => parseNumber(width) ?? 0),
        depths: matrix.depths.map(depth => parseNumber(depth) ?? 0),
        prices: matrix.prices.map(row => row.map(price => parseNumber(price))),
    };
};

// Shows the width x depth price matrix of a product in a view mode and an edit mode, with csv download and import
const ProductPriceMatrixTab: React.FC<ProductPriceMatrixTabProps> = ({ itemId, data, refetchData }) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { handleLogout } = useAuthContext();
    const userHasOnlyViewRights = useAllowedRight('only_view');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [editing, setEditing] = useState<boolean>(false);
    const [matrix, setMatrix] = useState<EditableMatrix>(toEditableMatrix(data?.price_matrix));
    const [saving, setSaving] = useState<boolean>(false);
    const [importing, setImporting] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Reload the editable matrix when the product data changes while not editing
    useEffect(() => {
        if (!editing) setMatrix(toEditableMatrix(data?.price_matrix));
    }, [data?.price_matrix]);

    // The matrix only applies to width x depth priced products
    const usesDimensions = data?.pricing_method_effective === 'dimensions';
    const savedMatrix = data?.price_matrix ?? null;
    const canEdit = !userHasOnlyViewRights;

    // Formats a price for the view mode
    const formatPrice = (price: number | null): string => {
        if (price === null || price === undefined) return '';
        return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);
    };

    // Updates the matrix in edit mode
    const updateMatrix = (updater: (current: EditableMatrix) => EditableMatrix) => {
        setMatrix(current => updater(current));
        setErrorMessage(null);
    };

    // Handles a width header change, only digits are kept
    const handleWidthChange = (index: number, value: string) => {
        updateMatrix(current => {
            const widths = [...current.widths];
            widths[index] = value.replace(/[^0-9]/g, '');
            return { ...current, widths };
        });
    };

    // Handles a depth header change, only digits are kept
    const handleDepthChange = (index: number, value: string) => {
        updateMatrix(current => {
            const depths = [...current.depths];
            depths[index] = value.replace(/[^0-9]/g, '');
            return { ...current, depths };
        });
    };

    // Handles a price cell change
    const handlePriceChange = (depthIndex: number, widthIndex: number, value: string) => {
        updateMatrix(current => {
            const prices = current.prices.map(row => [...row]);
            prices[depthIndex][widthIndex] = value;
            return { ...current, prices };
        });
    };

    // Adds a width column with empty prices
    const handleAddWidth = () => {
        updateMatrix(current => ({
            widths: [...current.widths, ''],
            depths: current.depths.length > 0 ? current.depths : [''],
            prices: (current.depths.length > 0 ? current.prices : [[]]).map(row => [...row, '']),
        }));
    };

    // Adds a depth row with empty prices
    const handleAddDepth = () => {
        updateMatrix(current => ({
            widths: current.widths.length > 0 ? current.widths : [''],
            depths: [...current.depths, ''],
            prices: [...current.prices, (current.widths.length > 0 ? current.widths : ['']).map(() => '')],
        }));
    };

    // Removes a width column
    const handleRemoveWidth = (index: number) => {
        updateMatrix(current => ({
            widths: current.widths.filter((_, widthIndex) => widthIndex !== index),
            depths: current.widths.length <= 1 ? [] : current.depths,
            prices: current.widths.length <= 1 ? [] : current.prices.map(row => row.filter((_, widthIndex) => widthIndex !== index)),
        }));
    };

    // Removes a depth row
    const handleRemoveDepth = (index: number) => {
        updateMatrix(current => ({
            widths: current.depths.length <= 1 ? [] : current.widths,
            depths: current.depths.filter((_, depthIndex) => depthIndex !== index),
            prices: current.prices.filter((_, depthIndex) => depthIndex !== index),
        }));
    };

    // Opens the edit mode, starting from the saved matrix
    const handleStartEditing = () => {
        if (!canEdit) return;
        setMatrix(toEditableMatrix(savedMatrix));
        setErrorMessage(null);
        setEditing(true);
    };

    // Leaves the edit mode without saving
    const handleCancel = () => {
        setMatrix(toEditableMatrix(savedMatrix));
        setErrorMessage(null);
        setEditing(false);
    };

    // Saves the matrix on the product and returns to the view mode
    const handleSave = async () => {
        setSaving(true);
        setErrorMessage(null);
        const response: any = await saveData({
            apiUrl: `patch_product/${itemId}`,
            method: 'patch',
            data: { price_matrix: toMatrixPayload(matrix) },
        }).catch((error: any) => error?.response);
        setSaving(false);

        // Show the validation message of the backend when the matrix is refused
        if (!response || response.status !== 200) {
            const message = response?.data?.price_matrix;
            setErrorMessage(Array.isArray(message) ? message[0] : (message || 'validation.price_matrix.invalid_structure'));
            return;
        }

        // Show the saved matrix, reload the product and confirm the save
        setMatrix(toEditableMatrix(response.data?.price_matrix));
        setEditing(false);
        setFloatingAlert({ type: 'success', message: 'product.price_matrix.saved_successfully' });
        if (refetchData) refetchData();
    };

    // Downloads the matrix as csv file
    const handleDownloadCsv = async () => {
        try {
            const blob = await fetchData({
                apiUrl: `get_product_price_matrix_csv/${itemId}`,
                responseAsBlob: true,
                handleLogout,
            });

            // Create a temporary link to trigger the download
            const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `price_matrix_${data?.number || itemId}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
        }
    };

    // Reads the selected csv file and imports it on the product
    const handleImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        // Read the file as data url and send it to the backend
        const reader = new FileReader();
        reader.onload = async (loadEvent: ProgressEvent<FileReader>) => {
            setImporting(true);
            setErrorMessage(null);
            const response: any = await saveData({
                apiUrl: `post_product_price_matrix_csv/${itemId}`,
                method: 'post',
                data: { file: loadEvent.target?.result, filename: file.name },
            }).catch((error: any) => error?.response);
            setImporting(false);

            // Show the validation message of the backend when the file is refused
            if (!response || response.status !== 200) {
                setErrorMessage(response?.data?.file || 'validation.price_matrix.invalid_file');
                return;
            }

            // Show the imported matrix and reload the product
            setMatrix(toEditableMatrix(response.data?.price_matrix));
            setEditing(false);
            setFloatingAlert({ type: 'success', message: 'product.price_matrix.imported_successfully' });
            if (refetchData) refetchData();
        };
        reader.readAsDataURL(file);
    };

    // Renders the saved matrix as a plain table
    const renderViewMatrix = () => {
        if (!savedMatrix || savedMatrix.widths.length === 0) {
            return <div className='add-item'>{t('product.price_matrix.empty')}</div>;
        }
        return (
            <table className='price-matrix-table view'>
                <thead>
                    <tr>
                        <th className='corner-cell'>{t('product.price_matrix.corner_label')}</th>
                        {savedMatrix.widths.map((width, widthIndex) => (
                            <th key={widthIndex} className='width-cell'>{width}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {savedMatrix.depths.map((depth, depthIndex) => (
                        <tr key={depthIndex}>
                            <th className='depth-cell'>{depth}</th>
                            {savedMatrix.widths.map((_, widthIndex) => (
                                <td key={widthIndex} className='price-cell'>{formatPrice(savedMatrix.prices[depthIndex]?.[widthIndex] ?? null)}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    // Renders the matrix with inputs and the add buttons
    const renderEditMatrix = () => (
        <table className='price-matrix-table'>
            <thead>
                <tr>
                    <th className='corner-cell'>{t('product.price_matrix.corner_label')}</th>
                    {matrix.widths.map((width, widthIndex) => (
                        <th key={widthIndex} className='width-cell'>
                            <div className='header-input'>
                                <input type='text' inputMode='numeric' value={width} placeholder={t('product.price_matrix.width_placeholder')} onChange={event => handleWidthChange(widthIndex, event.target.value)} />
                                <FontAwesomeIcon icon={faTrash} className='remove-icon' onClick={() => handleRemoveWidth(widthIndex)} />
                            </div>
                        </th>
                    ))}
                    <th className='add-cell'>
                        <div className='add-item' onClick={handleAddWidth}>{t('product.price_matrix.add_width_button')}</div>
                    </th>
                </tr>
            </thead>
            <tbody>
                {matrix.depths.map((depth, depthIndex) => (
                    <tr key={depthIndex}>
                        <th className='depth-cell'>
                            <div className='header-input'>
                                <input type='text' inputMode='numeric' value={depth} placeholder={t('product.price_matrix.depth_placeholder')} onChange={event => handleDepthChange(depthIndex, event.target.value)} />
                                <FontAwesomeIcon icon={faTrash} className='remove-icon' onClick={() => handleRemoveDepth(depthIndex)} />
                            </div>
                        </th>
                        {matrix.widths.map((_, widthIndex) => (
                            <td key={widthIndex} className='price-cell'>
                                <input type='text' inputMode='decimal' value={matrix.prices[depthIndex]?.[widthIndex] ?? ''} placeholder='0,00' onChange={event => handlePriceChange(depthIndex, widthIndex, event.target.value)} />
                            </td>
                        ))}
                        <td></td>
                    </tr>
                ))}
                <tr>
                    <th className='add-cell'>
                        <div className='add-item' onClick={handleAddDepth}>{t('product.price_matrix.add_depth_button')}</div>
                    </th>
                    {matrix.widths.map((_, widthIndex) => <td key={widthIndex}></td>)}
                    <td></td>
                </tr>
            </tbody>
        </table>
    );

    return (
        <div className='card product-price-matrix-tab'>
            <div className='price-matrix-header'>
                <div>
                    <h6>{t('product.price_matrix.title')}</h6>
                </div>
                {canEdit && (
                    <div className='price-matrix-actions'>
                        <input type='file' accept='.csv,.xlsx,.xls' ref={fileInputRef} onChange={handleImportFile} style={{ display: 'none' }} />
                        <SecondaryButton size='extra-small' label='product.price_matrix.import_csv_button' loading={importing} onClick={() => fileInputRef.current?.click()} />
                        <SecondaryButton size='extra-small' label='product.price_matrix.download_csv_button' onClick={handleDownloadCsv} />
                    </div>
                )}
            </div>
            {!usesDimensions && (
                <div className='alert form-alert alert-warning' role='alert'>{t('product.price_matrix.not_dimension_priced')}</div>
            )}
            {errorMessage && (
                <div className='alert form-alert alert-danger' role='alert'>{t(errorMessage, { defaultValue: errorMessage })}</div>
            )}
            <div className={`live-edit-field full-width-alignment ${canEdit ? 'editable' : ''}`}>
                {editing ? (
                    // Edit mode with the inputs and the save and cancel buttons
                    <div className='edit-mode'>
                        <div className='price-matrix-table-wrapper'>{renderEditMatrix()}</div>
                        <div className='button-row'>
                            <SecondaryButton size='extra-small' label='product.price_matrix.cancel_button' onClick={handleCancel} />
                            <PrimaryButton size='extra-small' label='product.price_matrix.save_button' loading={saving} onClick={handleSave} />
                        </div>
                    </div>
                ) : (
                    // View mode, click to edit
                    <div className='view-mode' onClick={handleStartEditing}>
                        <span className='price-matrix-table-wrapper'>{renderViewMatrix()}</span>
                        <span className='edit-icon'>
                            <FontAwesomeIcon icon={faPen} />
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductPriceMatrixTab;
